import React, { useEffect, useRef, Component } from 'react';
import * as THREE from 'three';
import { cn } from '../../utils/cn';
import { useIsMobile } from '../../hooks/useIsMobile';
type DottedSurfaceProps = Omit<ComponentProps<'div'>, 'ref'>;
export function DottedSurface({
  className,
  ...props
}: DottedSurfaceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const sceneRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    particles: THREE.Points[];
    animationId: number;
    count: number;
  } | null>(null);
  useEffect(() => {
    // Don't run Three.js on mobile
    if (isMobile) return;
    if (!containerRef.current) return;
    const container = containerRef.current;
    let width = container.clientWidth;
    let height = container.clientHeight;
    const SEPARATION = 140;
    const AMOUNTX = 30;
    const AMOUNTY = 45;
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0a0a0a, 1000, 3000);
    const camera = new THREE.PerspectiveCamera(60, width / height, 1, 10000);
    camera.position.set(0, 400, 1400);
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    const particles: THREE.Points[] = [];
    const positions: number[] = [];
    const colors: number[] = [];
    const geometry = new THREE.BufferGeometry();
    for (let ix = 0; ix < AMOUNTX; ix++) {
      for (let iy = 0; iy < AMOUNTY; iy++) {
        const x = ix * SEPARATION - AMOUNTX * SEPARATION / 2;
        const y = 0;
        const z = iy * SEPARATION - AMOUNTY * SEPARATION / 2;
        positions.push(x, y, z);
        if ((ix + iy) % 3 === 0) {
          colors.push(232 / 255, 120 / 255, 60 / 255);
        } else if ((ix + iy) % 3 === 1) {
          colors.push(200 / 255, 180 / 255, 160 / 255);
        } else {
          colors.push(232 / 255, 76 / 255, 26 / 255);
        }
      }
    }
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 5,
      vertexColors: true,
      transparent: true,
      opacity: 0.35,
      sizeAttenuation: true
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);
    let count = 0;
    let animationId: number;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      const positionAttribute = geometry.attributes.position;
      const positionsArray = positionAttribute.array as Float32Array;
      let i = 0;
      for (let ix = 0; ix < AMOUNTX; ix++) {
        for (let iy = 0; iy < AMOUNTY; iy++) {
          const index = i * 3;
          positionsArray[index + 1] = Math.sin((ix + count) * 0.3) * 25 + Math.sin((iy + count) * 0.5) * 25;
          i++;
        }
      }
      positionAttribute.needsUpdate = true;
      renderer.render(scene, camera);
      count += 0.04;
    };
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const {
          width: newWidth,
          height: newHeight
        } = entry.contentRect;
        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
      }
    });
    resizeObserver.observe(container);
    animate();
    sceneRef.current = {
      scene,
      camera,
      renderer,
      particles: [points],
      animationId,
      count
    };
    return () => {
      resizeObserver.disconnect();
      if (sceneRef.current) {
        cancelAnimationFrame(sceneRef.current.animationId);
        sceneRef.current.scene.traverse(object => {
          if (object instanceof THREE.Points) {
            object.geometry.dispose();
            if (Array.isArray(object.material)) {
              object.material.forEach(m => m.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
        sceneRef.current.renderer.dispose();
        if (container && sceneRef.current.renderer.domElement && container.contains(sceneRef.current.renderer.domElement)) {
          container.removeChild(sceneRef.current.renderer.domElement);
        }
      }
    };
  }, [isMobile]);
  // Mobile: Simple CSS dot pattern instead of Three.js
  if (isMobile) {
    return <div className={cn('pointer-events-none absolute inset-0 z-0', className)} style={{
      backgroundImage: `radial-gradient(circle, rgba(232, 76, 26, 0.15) 1px, transparent 1px)`,
      backgroundSize: '24px 24px'
    }} {...props} data-id="element-562" />;
  }
  return <div ref={containerRef} className={cn('pointer-events-none absolute inset-0 z-0', className)} {...props} data-id="element-563" />;
}